// Jour de double match (D-092) : deux équipes engagées d'un même groupe jouent le même jour.
//  - staff : sur la fiche de chacun des deux matchs, la carte « SF1 et SF2 jouent le même jour »
//    (les deux matchs, les compteurs), à la place de l'ancienne alerte ; l'écran « Répartir les
//    joueuses » : SF1 | SF2 | Les 2 pour chacune, puis « Annoncer les deux compositions » ;
//    les joueuses brûlées du groupe (« SF1 seulement »), dans la page de l'équipe ;
//  - joueuse, parent : son affectation du jour, une fois annoncée.
// Doubler reste toujours possible (le coach décide) : l'écran prévient seulement quand l'écart
// entre les coups d'envoi ne couvre pas le match, l'échauffement et la route.
// D-103 : un seul état — la répartition affichée est enregistrée dès l'affichage (jamais par-dessus un
// choix) ; « Annoncées le … · Réannoncer » ; la carte s'efface au coup d'envoi du premier match ; la
// joueuse et ses parents ont « Samedi : deux matchs » sur l'accueil, avant et après l'annonce.
import {
  eventById, matchDayAnnounce, matchDayPrefill, matchDaySet, matchLineup, matchPrep, matchTrip, myMatchDay, occurrenceRoster, responsesFor,
  setSquadLock, squadLocks, squadSetSheetMax, teamMatches, teamRoster, teamSetSheetMax,
  type Convoked, type MyDayRow, type RosterEntry, type SquadLock, type Team, type TeamMatch,
} from '../lib/api';
import { route } from '../lib/trip';
import { expectedOf, resolveAttendance, type AttendanceResponse } from '../domain/attendance';
import { attCtx, leadsFor, loadAvail } from './availability-data';
import { agree, displayName, type TeamGender } from '../domain/labels';
import {
  absentLine, availability, choices, countChips, crowKm, dayCounts, doubleNote, feasibility, overSheet, pickEvents, picksToSave, playerNote, roadEstimate,
  sheetMaxOf, shownPick, travelPlan, usualPick, type DayPlayerState, type Feasibility, type Pick, type Venue,
} from '../domain/match-day';
import { localDate, parseLocalDate } from '../domain/schedule';
import { duration, type TripInfo } from '../domain/trip';
import { hatShowing, squadName, switchHat, teamOf, type Ctx } from './context';
import { $, $$, armed, html, mount, openSheet, toast, type Html } from './dom';
import './indispos.css';
import { fillCrests, oppCrest } from './crest';
import { time } from './format';

/** Les autres matchs du groupe ce jour-là, dans une autre équipe engagée (D-042). */
export const sameDayMatches = (all: readonly TeamMatch[], eventId: string, squadId: string | null | undefined, date: string) =>
  (squadId ? all.filter(m => m.event_id !== eventId && m.squad_id && m.squad_id !== squadId && !m.events.cancelled_at
    && localDate(new Date(m.events.starts_at)) === date) : []);

interface Side {
  id: string; squadId: string; squad: string; start: Date; home: boolean | null; opponent: string; opponent_org: string | null;
  location: string | null; trip: TripInfo | null; announced: boolean; announcedAt: string | null;
}
interface Player { pid: string; name: string; num: number | null; st: DayPlayerState; usual: Pick; rowA?: boolean; rowB?: boolean; why: string | null;
  /** Incertaine (D-116) : « ? » ; sans échauffement à l'un des deux matchs (D-114). */
  unsure: boolean; late: string | null }
/** `unsaved` : la répartition proposée n'a pas pu être enregistrée (l'écran le dit). */
interface Day { team: Team; date: string; a: Side; b: Side; players: Player[]; absent: Player[]; unsaved: boolean }

/** « Annoncées le samedi 24 oct. à 14h05 ». */
const announcedLine = (iso: string) => {
  const d = new Date(iso);
  return `Annoncées le ${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })} à ${time(d)}`;
};

const weekday = (date: string) => parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'long' });
const dayShort = (date: string) => parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });

/** Tout ce qu'il faut pour répartir : les deux matchs (a = la première équipe du groupe), qui est convoquée,
 *  présente, brûlée, et les deux compositions. */
async function loadDay(ctx: Ctx, team: Team, pair: [TeamMatch, TeamMatch], date: string): Promise<Day> {
  const pos = (m: TeamMatch) => ctx.squads.find(q => q.id === m.squad_id)?.position ?? 0;
  const [ma, mb] = [...pair].sort((x, y) => pos(x) - pos(y) || (squadName(ctx, x.squad_id) ?? '').localeCompare(squadName(ctx, y.squad_id) ?? '')) as [TeamMatch, TeamMatch];
  const [roster, convA, convB, respA, respB, unavRows, luA, luB, locks, prepA, prepB, tripA, tripB] = await Promise.all([
    teamRoster(team.id), occurrenceRoster(ma.event_id, date), occurrenceRoster(mb.event_id, date),
    responsesFor(ma.event_id, date), responsesFor(mb.event_id, date), loadAvail(),
    matchLineup(ma.event_id), matchLineup(mb.event_id), squadLocks(team.id).catch(() => [] as SquadLock[]),
    matchPrep(ma.event_id).catch(() => null), matchPrep(mb.event_id).catch(() => null),
    matchTrip(ma.event_id).catch(() => null), matchTrip(mb.event_id).catch(() => null),
  ]);
  // Chaque match se juge à son heure et à sa route (D-114) : une règle « jusqu'à 12 h 45 » écarte du match de 13 h à
  // l'extérieur, pas de celui de 20 h 30 à domicile. Une incertaine est disponible, marquée « ? » (D-116).
  const leads = await leadsFor(team.club_id);
  const attend = (conv: Convoked[], resp: AttendanceResponse[], m: TeamMatch) => resolveAttendance(
    conv.filter(c => !c.invited).map(c => c.person_id), conv.filter(c => c.invited).map(c => c.person_id), resp,
    attCtx(unavRows, { kind: 'match' }, { date, start: new Date(m.events.starts_at) }, m, leads));
  const attA = attend(convA, respA, ma), attB = attend(convB, respB, mb);
  const expA = new Set(expectedOf(attA)), expB = new Set(expectedOf(attB));
  const side = (m: TeamMatch, announcedAt: string | null, trip: TripInfo | null): Side => ({
    id: m.event_id, squadId: m.squad_id!, squad: squadName(ctx, m.squad_id) ?? '', start: new Date(m.events.starts_at), home: m.is_home,
    opponent: m.opponent, opponent_org: m.opponent_org ?? null, location: m.events.location, trip, announced: !!announcedAt, announcedAt,
  });
  const a = side(ma, prepA?.lineup_announced_at ?? null, tripA), b = side(mb, prepB?.lineup_announced_at ?? null, tripB);
  const members = new Map(roster.filter(r => r.kind === 'member').map(r => [r.person_id, r]));
  const convoked = new Map([...convA, ...convB].map(c => [c.person_id, c]));
  const selA = new Map(luA.map(l => [l.person_id, l.selected])), selB = new Map(luB.map(l => [l.person_id, l.selected]));
  const lockOf = new Map(locks.map(l => [l.person_id, l.squad_id]));
  const players: Player[] = [], absent: Player[] = [];
  for (const [pid, c] of convoked) {
    const r = members.get(pid);
    const conv = { a: convA.some(x => x.person_id === pid), b: convB.some(x => x.person_id === pid) };
    const avail = availability(conv, { a: expA.has(pid), b: expB.has(pid) });
    const lk = lockOf.get(pid);
    const off = attA.absent.find(x => x.personId === pid) ?? attB.absent.find(x => x.personId === pid);
    const p: Player = {
      pid, name: displayName(c), num: r?.jersey_number ?? c.jersey_number ?? null,
      st: { member: !!r, conv, avail, lock: lk === a.squadId ? 'a' : lk === b.squadId ? 'b' : null },
      usual: usualPick(r?.squad_ids, a.squadId, b.squadId), rowA: selA.get(pid), rowB: selB.get(pid),
      why: off ? (off.medical ? 'médical' : off.source === 'unavailability' ? 'indispo' : off.source === 'rule' ? off.label ?? 'indispo' : off.reason || null) : null,   // D-009 : jamais la nature
      unsure: attA.uncertain.some(u => u.personId === pid) || attB.uncertain.some(u => u.personId === pid),
      late: attA.late.has(pid) ? a.squad : attB.late.has(pid) ? b.squad : null,
    };
    (avail.a || avail.b ? players : absent).push(p);
  }
  const byName = (x: Player, y: Player) => x.name.localeCompare(y.name, 'fr');
  // Un seul état (D-103) : ce que l'écran propose (l'équipe habituelle) est enregistré tout de suite,
  // sans jamais remplacer un choix ; la composition, « À faire » et les stats comptent alors la même chose.
  let unsaved = false;
  const toSave = picksToSave(players, shownOf);
  if (toSave.length) {
    try {
      await matchDayPrefill(a.id, b.id, toSave.map(x => ({ person_id: x.pid, in: pickEvents(x.pick, a.id, b.id) })));
      for (const x of toSave) { const p = players.find(y => y.pid === x.pid)!; p.rowA = x.pick !== 'b'; p.rowB = x.pick !== 'a'; }
    } catch { unsaved = true; }
  }
  return { team, date, a, b, players: players.sort(byName), absent: absent.sort(byName), unsaved };
}

/** Le choix affiché de chacune (écrit, sinon son équipe habituelle si elle est ouverte). */
function shownOf(p: Player): Pick { return shownPick(p.rowA, p.rowB, p.usual, choices(p.st)); }

/** « à domicile », « à l'extérieur · 35 min » (trajet depuis notre gymnase, s'il est connu). */
const whereOf = (s: Side) => (s.home ? 'à domicile' : s.home === false ? `à l’extérieur${s.trip?.trip ? ` · ${duration(s.trip.trip.min)}` : ''}` : 'terrain neutre');

/** Staff, fiche d'un des deux matchs : la carte du jour, à la place de l'ancienne alerte « SF2 joue aussi ce jour-là ». */
export async function renderDayCards(el: HTMLElement, ctx: Ctx, team: Team, me: TeamMatch, others: TeamMatch[], date: string): Promise<void> {
  // Le premier match a commencé : la répartition est jouée, la carte s'efface (D-103).
  const now = Date.now();
  const open = others.filter(o => Math.min(Date.parse(me.events.starts_at), Date.parse(o.events.starts_at)) > now);
  const cards = await Promise.all(open.map(async o => {
    const d = await loadDay(ctx, team, [me, o], date);
    const c = dayCounts(d.players.map(shownOf));
    const [first, second] = [d.a, d.b].sort((x, y) => x.start.getTime() - y.start.getTime()) as [Side, Side];
    const announced = d.a.announced && d.b.announced;
    const at = [d.a.announcedAt, d.b.announcedAt].filter((x): x is string => !!x).sort().pop();
    return html`<section class="block rp-card" data-guide="ev-day">
      <p class="eyebrow">${dayShort(date)} · deux matchs</p>
      <h3>${d.a.squad} et ${d.b.squad} jouent le même jour</h3>
      <div class="rp-two">${[first, second].map(s => html`<div class="rp-m"><span class="n">${s.squad} · ${time(s.start)}</span><b class="crest-line">${oppCrest(s, 'opp-crest-xs')}<span>${s.opponent}</span></b><span>${whereOf(s)}</span></div>`)}</div>
      <div class="rp-sum">${countChips(c, d.a.squad, d.b.squad).map(x => html`<span class="chip ${x.kind === 'ok' ? 'chip-ok' : x.kind === 'both' ? 'chip-both' : 'chip-warn'}">${x.label}</span>`)}</div>
      <p class="hint rp-state">${announced && at ? `${announcedLine(at)} : ${agree(team.gender, 'chacun sait où il joue', 'chacune sait où elle joue')}.`
        : d.unsaved ? 'Répartition proposée, pas encore enregistrée : l’annonce l’enregistre.'
        : `Enregistrée, pas encore annoncée : ${agree(team.gender, 'les joueurs ne la voient', 'les joueuses ne la voient')} qu’à l’annonce.`}</p>
      <a class="btn ${announced ? 'btn-ghost' : 'btn-primary'}" href="#/repartir/${me.event_id}/${o.event_id}">${announced ? 'Revoir et réannoncer' : agree(team.gender, 'Répartir les joueurs', 'Répartir les joueuses')} →</a>
    </section>`;
  }));
  mount(el, html`${cards}`);
  void fillCrests(el);
}

/** La route entre les deux salles : le trajet en cache, l'itinéraire (OSRM), sinon une estimation. */
async function dayTravel(a: Side, b: Side): Promise<number | null> {
  const venue = (s: Side): Venue => ({ home: s.home, at: s.trip?.to ?? (s.home ? s.trip?.from ?? null : null), cachedMin: s.trip?.trip?.min ?? null });
  const plan = travelPlan(venue(a), venue(b));
  if (!('route' in plan)) return plan.min;
  const [x, y] = plan.route;
  const r = await route({ ...x, label: null }, { ...y, label: null }).catch(() => null);
  return r ? r.min : roadEstimate(crowKm(x, y));
}

/** L'écran « Répartir les joueuses » (#/repartir/<match>/<autre match>). */
export async function renderMatchDay(root: HTMLElement, ctx: Ctx, eventId: string, otherId: string): Promise<void> {
  const ev = await eventById(eventId);
  const otherHat = ev ? hatShowing(ctx, ev.team_id) : null;   // casquettes (D-072)
  if (otherHat) { switchHat(ctx, otherHat); window.dispatchEvent(new HashChangeEvent('hashchange')); return; }
  const team = ev ? teamOf(ctx, ev.team_id) : null;
  const all = team ? await teamMatches(team.id) : [];
  const me = all.find(m => m.event_id === eventId), other = all.find(m => m.event_id === otherId);
  const date = me ? localDate(new Date(me.events.starts_at)) : '';
  if (!team || !me || !other || !ctx.staffOf.has(team.id) || !sameDayMatches(all, eventId, me.squad_id, date).includes(other)) {
    mount(root, html`<section class="screen"><a class="back" href="#/" data-back>‹ Retour</a>
      <div class="empty"><p class="empty-title">Répartition indisponible.</p><p>Elle est réservée au staff, pour deux matchs du groupe le même jour.</p></div></section>`);
    return;
  }
  const d = await loadDay(ctx, team, [me, other], date);
  const g: TeamGender = team.gender;
  const picks = new Map(d.players.map(p => [p.pid, shownOf(p)]));
  const open = new Map(d.players.map(p => [p.pid, choices(p.st)]));
  const [first, second] = [d.a, d.b].sort((x, y) => x.start.getTime() - y.start.getTime()) as [Side, Side];
  let f: Feasibility | null = null;
  const lockName = (p: Player) => (p.st.lock === 'a' ? d.a.squad : p.st.lock === 'b' ? d.b.squad : null);
  const onlyFor = (p: Player) => (!p.st.member ? null : p.st.avail.a && !p.st.avail.b ? d.b.squad : p.st.avail.b && !p.st.avail.a ? d.a.squad : null);
  const sideInfo = (s: Side) => `${s.opponent} · ${s.home ? 'domicile' : s.home === false ? (s.trip?.trip ? `${duration(s.trip.trip.min)} de route` : 'extérieur') : 'terrain neutre'}`;
  // Annoncées (D-103) : la date de l'annonce, et « Réannoncer » (seules celles dont l'équipe change sont prévenues de nouveau).
  const announcedAt = d.a.announced && d.b.announced ? [d.a.announcedAt, d.b.announcedAt].filter((x): x is string => !!x).sort().pop() ?? null : null;
  const annLabel = announcedAt ? 'Réannoncer les deux compositions' : 'Annoncer les deux compositions';

  mount(root, html`
    <section class="screen rp-screen">
      <a class="back" href="#/e/${eventId}/${date}" data-back>‹ ${squadName(ctx, me.squad_id) ?? team.name} – ${me.opponent}</a>
      <header class="screen-head"><p class="eyebrow">${team.name} · ${dayShort(date)}</p><h2>${agree(g, 'Répartir les joueurs', 'Répartir les joueuses')}</h2></header>
      <div class="rp-two" id="rp-heads"></div>
      <p class="hint rp-sheet" id="rp-sheet"></p>
      <div id="rp-note"><div class="rp-ok wait"><span aria-hidden="true">…</span><span>Calcul du trajet entre les deux salles.</span></div></div>
      <section class="block rp-block">
        <div class="rp-head"><span>N°</span><span>${agree(g, 'Joueur', 'Joueuse')} · ${weekday(date)}</span><span>Joue en</span></div>
        <ul class="rp-list" id="rp-list"></ul>
        ${d.players.length ? '' : html`<p class="muted">Personne n’est disponible ce jour-là.</p>`}
      </section>
      <div class="rp-foot">
        ${announcedAt ? html`<p class="rp-done" data-guide="rp-done"><span class="chip chip-ok">✓ ${announcedLine(announcedAt)}</span></p>` : ''}
        <button type="button" class="btn ${announcedAt ? 'btn-ghost' : 'btn-primary'}" id="rp-announce" data-guide="rp-announce">${annLabel}</button>
        <p class="hint">${announcedAt ? `Réannoncer : ${agree(g, 'seuls ceux', 'seules celles')} dont l’équipe change ${agree(g, 'sont prévenus', 'sont prévenues')} de nouveau.`
          : html`${agree(g, 'Chaque joueur est prévenu', 'Chaque joueuse est prévenue')} : avec quelle équipe, où et à quelle heure (les deux matchs ${agree(g, 's’il double', 'si elle double')}).`}
          ${d.unsaved ? 'Répartition proposée, pas encore enregistrée : l’annonce l’enregistre.' : announcedAt ? '' : `Tout est enregistré au fil des choix ; ${agree(g, 'les joueurs ne voient', 'les joueuses ne voient')} rien avant l’annonce.`}
          ${absentLine(g, weekday(date), d.absent.map(p => ({ name: p.name, why: p.why })))}</p>
      </div>
    </section>`);

  const heads = $(root, '#rp-heads')!, list = $(root, '#rp-list')!, note = $(root, '#rp-note')!;
  const ann = $<HTMLButtonElement>(root, '#rp-announce')!;
  // Joueuses sur la feuille : le repère de chaque équipe engagée (réglable, 12 par défaut).
  const maxOf = (s: Side) => sheetMaxOf(team, ctx.squads.find(q => q.id === s.squadId));
  const paint = () => {
    const c = dayCounts(d.players.map(p => picks.get(p.pid) ?? null));
    mount(heads, html`${([[d.a, c.a], [d.b, c.b]] as [Side, number][]).map(([s, n]) => html`<div class="rp-m">
      <span class="n ${n > maxOf(s) ? 'over' : ''}">${s.squad} · ${n} / ${maxOf(s)}</span><b>${time(s.start)}</b><span>${sideInfo(s)}</span>
      ${overSheet(n, maxOf(s)) ? html`<small class="rp-over">${overSheet(n, maxOf(s))} (${maxOf(s)} au plus)</small>` : ''}</div>`)}`);
    mount(list, html`${d.players.map(p => {
      const pick = picks.get(p.pid) ?? null, o = open.get(p.pid)!;
      const tight = pick === 'both' && !!f && !f.ok;
      const btn = (k: 'a' | 'b' | 'both', label: string) => html`<button type="button" data-pick="${k}" class="${pick === k ? (k === 'both' ? 'on both' : 'on') : ''}"
        aria-pressed="${String(pick === k)}" ${o[k] || pick === k ? '' : 'disabled'}>${label}</button>`;
      return html`<li class="${pick ? '' : 'todo-p'}" data-pid="${p.pid}">
        <span class="nb">${p.num ?? ''}</span>
        <span class="who"><strong>${p.name}${p.unsure ? html` <b class="ix-unc">?</b>` : ''}</strong><small>${playerNote(g, { lockName: lockName(p), todo: !pick, renfort: !p.st.member, onlyFor: onlyFor(p) })}${p.unsure ? ` · ${agree(g, 'incertain', 'incertaine')}` : ''}${p.late ? ` · sans échauffement (${p.late})` : ''}${tight ? html` <span class="rp-tight">écart court</span>` : ''}</small></span>
        <span class="seg3" role="group" aria-label="${p.name} joue en">${btn('a', d.a.squad)}${btn('b', d.b.squad)}${btn('both', 'Les 2')}</span>
      </li>`;
    })}`);
    // Un choix changé après « Confirmer ? » : on redemande.
    if (ann.dataset.armed) { delete ann.dataset.armed; ann.classList.remove('armed'); ann.textContent = annLabel; }
  };
  const sheetLine = () => mount($(root, '#rp-sheet')!, html`Feuille de match : ${d.a.squad} ${maxOf(d.a)} · ${d.b.squad} ${maxOf(d.b)} ${agree(g, 'joueurs', 'joueuses')} au plus. <button type="button" class="link-x" id="rp-sheet-edit">changer</button>`);
  sheetLine();
  $(root, '#rp-sheet')!.addEventListener('click', e => {
    if (!(e.target as HTMLElement).closest('#rp-sheet-edit')) return;
    openSheet('Feuille de match', html`<form class="form" id="f-sheet" novalidate>
      ${[d.a, d.b].map(s => html`<label class="field"><span>${s.squad} : ${agree(g, 'joueurs', 'joueuses')} au plus</span><input name="${s.squadId}" type="number" inputmode="numeric" min="5" max="20" value="${maxOf(s)}"></label>`)}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
      <p class="hint">Pour tous les matchs de chaque équipe. Un repère pour le compteur : rien n’est bloqué.</p></form>`, (sheet, close) => {
      const f = $<HTMLFormElement>(sheet, '#f-sheet')!;
      f.addEventListener('submit', async ev => {
        ev.preventDefault();
        const vals = [d.a, d.b].map(s => ({ s, n: Number(new FormData(f).get(s.squadId)) }));
        if (vals.some(v => !Number.isInteger(v.n) || v.n < 5 || v.n > 20)) { $(f, '.form-msg')!.textContent = 'Entre 5 et 20.'; return; }
        try {
          for (const { s, n } of vals) {
            const q = ctx.squads.find(x => x.id === s.squadId);
            if (q) { await squadSetSheetMax(q.id, n); q.sheet_max = n; } else { await teamSetSheetMax(team.id, n); team.sheet_max = n; }
          }
          close(); toast('Enregistré'); sheetLine(); paint();
        } catch { $(f, '.form-msg')!.textContent = 'Impossible pour le moment.'; }
      });
    });
  });
  paint();

  list.addEventListener('click', async (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-pick]');
    if (!b || b.disabled) return;
    const pid = b.closest<HTMLElement>('[data-pid]')!.dataset.pid!;
    const k = b.dataset.pick as 'a' | 'b' | 'both';
    const before = picks.get(pid) ?? null;
    const next: Pick = before === k ? null : k;   // toucher le choix en cours la remet « à placer »
    picks.set(pid, next); paint();
    try { await matchDaySet(d.a.id, d.b.id, pid, pickEvents(next, d.a.id, d.b.id)); d.unsaved = false; }
    catch (err) {
      picks.set(pid, before); paint();
      toast(/brûl/.test((err as Error).message) ? `${agree(g, 'Joueur brûlé', 'Joueuse brûlée')} : ${agree(g, 'il', 'elle')} ne joue que dans son équipe.` : 'Impossible pour le moment.', 'ko');
    }
  });

  ann.addEventListener('click', async () => {
    const c = dayCounts(d.players.map(p => picks.get(p.pid) ?? null));
    if (!armed(ann, c.todo ? `Confirmer : ${c.todo} à placer ne ${c.todo > 1 ? 'jouent' : 'joue'} pas` : 'Confirmer : annoncer et prévenir')) return;
    ann.disabled = true;
    try {
      const n = await matchDayAnnounce(d.a.id, d.b.id, [
        ...d.players.map(p => ({ person_id: p.pid, in: pickEvents(picks.get(p.pid) ?? null, d.a.id, d.b.id) })),
        ...d.absent.map(p => ({ person_id: p.pid, in: [] as string[] })),
      ]);
      toast(n ? `Compositions annoncées (${n} ${n > 1 ? 'comptes prévenus' : 'compte prévenu'})` : 'Compositions mises à jour');
      await renderMatchDay(root, ctx, eventId, otherId);
    } catch (err) {
      ann.disabled = false;
      toast(/brûl/.test((err as Error).message) ? `${agree(g, 'Un joueur brûlé est placé', 'Une joueuse brûlée est placée')} hors de son équipe : change son choix.` : 'Annonce impossible.', 'ko');
    }
  });

  // L'écart entre les deux coups d'envoi, la route comprise : un avertissement, jamais un refus.
  const travel = await dayTravel(first, second);
  f = feasibility(first.start, second.start, travel);
  const n = doubleNote(f, first, second);
  mount(note, html`<div class="rp-ok ${n.ok ? 'yes' : 'warn'}" role="status"><span aria-hidden="true">${n.ok ? '✓' : '!'}</span>
    <span><b>${n.lead}</b> : ${n.detail}${n.ok ? '' : ` Le doublé reste possible : à toi de voir.`}</span></div>`);
  if (!f.ok) paint();
}

/** Un match du jour, vu par la joueuse ou le parent : équipe, heure, adversaire, salle. */
interface DayMatch { event_id: string; squad: string; starts_at: string; is_home: boolean | null; opponent: string; place: string | null }
const dayLine = (r: DayMatch) => `${r.squad} à ${time(new Date(r.starts_at))}, ${r.is_home ? `à domicile contre ${r.opponent}` : r.is_home === false ? `chez ${r.opponent}` : `contre ${r.opponent}`}${r.place ? ` (${r.place})` : ''}`;
const toDayMatch = (ctx: Ctx, m: TeamMatch): DayMatch => ({
  event_id: m.event_id, squad: squadName(ctx, m.squad_id) ?? '', starts_at: m.events.starts_at, is_home: m.is_home, opponent: m.opponent, place: m.events.location,
});

/** « Samedi : deux matchs » (D-092, D-103) : les deux matchs du jour ; avant l'annonce, « la répartition
 *  arrive » ; annoncée, la place de chacune des miennes (« Tu joues… », « Kenza joue… »), jamais celle des autres. */
function myDayCard(ctx: Ctx, matches: DayMatch[], rows: MyDayRow[], people: string[], gender: TeamGender, link: string | null): Html {
  const sorted = [...matches].sort((x, y) => x.starts_at.localeCompare(y.starts_at));
  const date = localDate(new Date(sorted[0]!.starts_at));
  const days = Math.round((parseLocalDate(date).getTime() - parseLocalDate(localDate(new Date())).getTime()) / 86400_000);
  const wd = weekday(date);
  const title = days >= 0 && days < 7 ? wd.charAt(0).toUpperCase() + wd.slice(1) : parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' }).replace(/^./, c => c.toUpperCase());
  const announced = rows.length > 0 && rows.every(r => r.announced);
  const isSelf = (pid: string) => { const p = ctx.people.find(x => x.id === pid); return !p || p.user_id === ctx.uid; };
  const nameOf = (pid: string) => { const p = ctx.people.find(x => x.id === pid); return p ? displayName(p) : ''; };
  const sentence = (pid: string) => {
    const self = isSelf(pid);
    const who = self ? 'Tu' : nameOf(pid);
    const plays = rows.filter(r => r.person_id === pid && r.plays).sort((x, y) => x.starts_at.localeCompare(y.starts_at));
    const verb = self ? 'joues' : 'joue';
    if (plays.length > 1) return { ok: true, text: `${who} ${verb} les deux matchs : ${dayLine(plays[0]!)}, puis ${dayLine(plays[1]!)}.` };
    if (plays.length) return { ok: true, text: `${who} ${verb} avec ${dayLine(plays[0]!)}.` };
    return { ok: false, text: `${who} ${self ? 'n’es' : 'n’est'} pas ${agree(gender, 'retenu', 'retenue')} ${wd}.` };
  };
  const who = people.length === 1 && !isSelf(people[0]!) ? nameOf(people[0]!) : null;
  return html`<section class="block md-day" data-guide="md-day">
    <h3>${title} : deux matchs</h3>
    <ul class="md-two">${sorted.map(m => html`<li><b>${m.squad} · ${time(new Date(m.starts_at))}</b><span>${m.is_home ? `à domicile contre ${m.opponent}` : m.is_home === false ? `chez ${m.opponent}` : `contre ${m.opponent}`}</span></li>`)}</ul>
    ${announced ? people.map(pid => { const s = sentence(pid); return html`<p class="mb-me ${s.ok ? 'ok' : ''}">${s.text}</p>`; })
      : html`<p class="mb-me md-wait">La répartition arrive : le coach dit bientôt ${who ? `où joue ${who}` : people.length > 1 ? 'qui joue où' : 'où tu joues'} ; une notification te préviendra.</p>`}
    ${link ? html`<a class="btn btn-ghost btn-sm" href="${link}">Voir le match →</a>` : ''}
  </section>`;
}

/** Fiche du match, joueuse ou parent : « Samedi : deux matchs », avant l'annonce (« la répartition arrive ») et après. */
export async function renderMyDay(el: HTMLElement, ctx: Ctx, eventId: string, gender: TeamGender, day: TeamMatch[] = []): Promise<void> {
  const rows = await myMatchDay(eventId).catch(() => [] as MyDayRow[]);
  const matches = day.length > 1 ? day.map(m => toDayMatch(ctx, m)) : [...new Map(rows.map(r => [r.event_id, r as DayMatch])).values()];
  if (matches.length < 2) { mount(el, html``); return; }
  const teamId = day[0]?.events.team_id;
  const people = rows.length ? [...new Set(rows.map(r => r.person_id))]
    : ctx.people.filter(p => ctx.memberships.some(m => m.person_id === p.id && m.team_id === teamId)).map(p => p.id);
  if (!people.length) { mount(el, html``); return; }
  mount(el, myDayCard(ctx, matches, rows, people, gender, null));
}

/** Accueil, joueuse ou parent (D-103) : « Samedi : deux matchs » quand le groupe de l'une des miennes joue
 *  deux matchs le même jour, dans les 7 jours (avant l'annonce : « la répartition arrive »). */
export async function renderHomeDay(el: HTMLElement, ctx: Ctx): Promise<void> {
  const teamIds = [...new Set(ctx.memberships.filter(m => ctx.people.some(p => p.id === m.person_id)).map(m => m.team_id))]
    .filter(t => !ctx.staffOf.has(t) && ctx.squads.filter(q => q.team_id === t).length > 1);
  if (!teamIds.length) { mount(el, html``); return; }
  const now = Date.now(), horizon = now + 7 * 86400_000;
  const cards = (await Promise.all(teamIds.map(async teamId => {
    const all = await teamMatches(teamId).catch(() => [] as TeamMatch[]);
    const soon = all.filter(m => m.squad_id && !m.events.cancelled_at && Date.parse(m.events.starts_at) > now && Date.parse(m.events.starts_at) < horizon);
    const byDay = new Map<string, TeamMatch[]>();
    for (const m of soon) { const d = localDate(new Date(m.events.starts_at)); byDay.set(d, [...(byDay.get(d) ?? []), m]); }
    const pair = [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, l]) => l).find(l => new Set(l.map(m => m.squad_id)).size > 1);
    if (!pair) return null;
    const day = pair.sort((x, y) => x.events.starts_at.localeCompare(y.events.starts_at)).slice(0, 2);
    const rows = await myMatchDay(day[0]!.event_id).catch(() => [] as MyDayRow[]);
    const people = rows.length ? [...new Set(rows.map(r => r.person_id))]
      : ctx.people.filter(p => ctx.memberships.some(m => m.person_id === p.id && m.team_id === teamId)).map(p => p.id);
    if (!people.length) return null;
    // Le match où joue l'une des miennes (annoncé), sinon le premier.
    const mine = rows.find(r => r.plays && r.announced)?.event_id ?? day[0]!.event_id;
    const gender = teamOf(ctx, teamId)?.gender ?? 'X';
    return myDayCard(ctx, day.map(m => toDayMatch(ctx, m)), rows, people, gender, `#/e/${mine}/${localDate(new Date(day[0]!.events.starts_at))}`);
  }))).filter((x): x is Html => x !== null);
  mount(el, html`${cards}`);
}

/** Page de l'équipe (staff) : les joueuses brûlées du groupe, une liste par saison (une équipe d'Arceau vit une saison). */
export async function renderSquadLocks(el: HTMLElement, ctx: Ctx, team: Team, roster: RosterEntry[]): Promise<void> {
  const squads = ctx.squads.filter(q => q.team_id === team.id).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  if (squads.length < 2 || !ctx.staffOf.has(team.id)) { mount(el, html``); return; }
  const locks = await squadLocks(team.id).catch(() => [] as SquadLock[]);
  const g = team.gender;
  const reload = () => renderSquadLocks(el, ctx, team, roster);
  const nameOf = (pid: string) => { const r = roster.find(x => x.person_id === pid); return r ? displayName(r) : '—'; };
  const sqName = (id: string) => squads.find(q => q.id === id)?.name ?? '—';
  const free = roster.filter(r => !locks.some(l => l.person_id === r.person_id));
  mount(el, html`
    <section class="block">
      <div class="block-head"><h3>${agree(g, 'Joueurs brûlés', 'Joueuses brûlées')}${locks.length ? html` <span class="muted small">${locks.length}</span>` : ''}</h3>
        ${free.length ? html`<button type="button" class="btn btn-ghost btn-sm" id="sl-add">${agree(g, '+ Joueur', '+ Joueuse')}</button>` : ''}</div>
      ${locks.length ? html`<ul class="ce-rows">${locks.map(l => html`<li><span><strong>${nameOf(l.person_id)}</strong> <span class="chip chip-mute">${sqName(l.squad_id)} seulement</span></span>
        <button type="button" class="link-x" data-sl-del="${l.person_id}" aria-label="Retirer ${nameOf(l.person_id)} de la liste">retirer</button></li>`)}</ul>` : ''}
      <p class="hint">Cette saison, ${agree(g, 'ils ne jouent', 'elles ne jouent')} que dans leur équipe : quand ${squads[0]!.name} et ${squads[1]!.name} jouent le même jour, les autres choix sont grisés pour ${agree(g, 'eux', 'elles')}. Règles FFBB à vérifier avec ta ligue : l’app n’impose rien.</p>
    </section>`);
  $(el, '#sl-add')?.addEventListener('click', () => {
    openSheet(agree(g, 'Joueur brûlé', 'Joueuse brûlée'), html`
      <label class="field"><span>Ne joue qu'en</span><select id="sl-sq">${squads.map((q, i) => html`<option value="${q.id}" ${i === 0 ? 'selected' : ''}>${q.name} seulement</option>`)}</select></label>
      <ul class="pick-list">${free.map(r => html`<li><button type="button" data-sl-pick="${r.person_id}"><span class="pick-main"><strong>${displayName(r)}</strong>${r.jersey_number != null ? html` <span class="muted small">n° ${r.jersey_number}</span>` : ''}</span><span class="chip chip-ok">Choisir</span></button></li>`)}</ul>
      <p class="hint">Règles FFBB à vérifier avec ta ligue.</p>`, (sheet, close) => {
      for (const b of $$<HTMLButtonElement>(sheet, '[data-sl-pick]')) b.addEventListener('click', async () => {
        const sq = $<HTMLSelectElement>(sheet, '#sl-sq')!.value;
        try { await setSquadLock(team.id, b.dataset.slPick!, sq); close(); toast(`${nameOf(b.dataset.slPick!)} : ${sqName(sq)} seulement`); await reload(); }
        catch { toast('Impossible pour le moment.', 'ko'); }
      });
    });
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-sl-del]')) b.addEventListener('click', async () => {
    try { await setSquadLock(team.id, b.dataset.slDel!, null); toast(agree(g, 'Retiré de la liste', 'Retirée de la liste')); await reload(); }
    catch { toast('Impossible pour le moment.', 'ko'); }
  });
}
